import ShuffledText from "@/mono/components/ShuffledText";
import { useScrollRigScrollTrigger } from "@/mono/lib/hooks/useScrollRigScrollTrigger";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";

import { TransitionLink } from "@/mono/components/TransitionLink";

import useResize from "@/mono/lib/hooks/useResize";
import { mediaTabletDown as mediaMobile } from "@/mono/lib/responsive";
import { setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";
import SplitText from "mono/components/SplitText";
import styled, { css } from "styled-components";
import NineGLImageElement from "./gl/NineGLImage/NineGLImageElement";
import { mediaTabletOnly } from "mono/lib/responsive";

export const Project = forwardRef(
  (
    {
      i,
      title,
      slug,
      year,
      deliverables: _deliverables,
      coverImage: _coverImage,
      alignMeta = "top",
      isLink = true,
      scrollSceneConfig = {},
      animateOnScroll = true,
      ...props
    },
    ref,
  ) => {
    const wrapper = useRef();
    const el = useRef();

    const titleRef = useRef();

    const glImage = useRef();

    const yearRef = useRef();
    const deliverables = useRef([]);

    function animateIn() {
      glImage.current?.animateIn?.();
      titleRef.current?.animateIn?.({ delay: 0.4 });
      [...deliverables.current].forEach((r, i) =>
        r?.animateIn?.({
          delay: i * 0.2,
        }),
      );
    }

    useImperativeHandle(ref, () => ({
      animateIn,
    }));

    useScrollRigScrollTrigger({
      el: animateOnScroll ? el : null,
      offset: 0.4,

      handleIn: () => {
        if (!animateOnScroll) return;
        animateIn();
      },
    });

    const checkIsVideo = (url) =>
      url.includes(".mp4") ||
      url.includes(".webm") ||
      url.includes(".m3u8") ||
      url.includes(".mov");

    const coverImage = useMemo(() => {
      if (typeof _coverImage === "string") {
        return { url: _coverImage };
      }
      return { ..._coverImage };
    }, [_coverImage]);

    const isVideo = useMemo(() => {
      if (coverImage?.video) {
        return true;
      }
      return checkIsVideo(coverImage?.url);
    }, [coverImage?.url]);

    const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);
    // const [isVertical, setIsVertical] = useState(isMobileLayout);
    const isVertical = true;

    useResize(
      () => {
        if (isMobileLayout) return;

        // setIsVertical(
        //   wrapper.current.clientWidth <= wrapper.current.clientHeight
        // );
      },
      { immediate: true },
      [isMobileLayout, wrapper.current],
    );

    const meta = {
      year,
      deliverables: _deliverables,
    };

    const renderProjectMeta = useMemo(
      () => (
        <>
          {Object.keys(meta).length > 0 && (
            <ProjectMeta $renderBottom={alignMeta === "bottom" || isVertical}>
              {/* {meta?.year && (
                <ShuffledText
                  noMeasure
                  text={String(meta.year)}
                  ref={yearRef}
                  animateOnScroll={false}
                ></ShuffledText>
              )} */}
              {meta?.deliverables?.length > 0 &&
                meta.deliverables.map(({ title }, i) => (
                  <ShuffledText
                    noMeasure
                    key={i}
                    text={title}
                    ref={(r) => (deliverables.current[i] = r)}
                    animateOnScroll={false}
                  ></ShuffledText>
                ))}
            </ProjectMeta>
          )}
        </>
      ),
      [meta, alignMeta, isVertical],
    );

    if (!coverImage?.url) return null;

    return (
      <StyledProject
        ref={wrapper}
        $isLink={isLink}
        className={`project-img project-${i}`}
      >
        <ProjectTitleWrapper ref={el}>
          <SplitText
            tag="h3"
            ref={titleRef}
            className="project-title"
            animateOnScroll={false}
            animation="charDoubleClipped"
            type="chars"
          >
            {title}
          </SplitText>
          {!isVertical ? renderProjectMeta : null}
        </ProjectTitleWrapper>

        <TransitionLink href={`/project/${slug}`} {...props}>
          <NineGLImageElement
            src={coverImage.url}
            animateOnScroll={false}
            offset={-0.5}
            isVideo={isVideo}
            ref={glImage}
            alt={title}
            className="project-image"
            isLink={isLink}
            i={i}
            {...coverImage}
          />
        </TransitionLink>
        {isVertical ? renderProjectMeta : null}
      </StyledProject>
    );
  },
);

const ProjectTitleWrapper = styled.div`
  width: 100%;

  /* height: 100rem; */
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  position: relative;

  margin-bottom: 30rem;
`;

const StyledProject = styled.div`
  position: absolute;
  cursor: ${({ $isLink }) => ($isLink ? "pointer" : "default")};

  a {
    display: block !important;
  }

  .project-title {
    ${setTypography("projectsTitle")}
  }

  ${mediaMobile(css`
    left: unset !important;
    top: unset !important;
    right: unset !important;
    bottom: unset !important;
    width: 100% !important;
    position: relative !important;
    transform: none !important;

    margin-bottom: 130rem;
  `)}

  ${mediaTabletOnly(`
    width: 50%;
  `)}
`;

const ProjectMeta = styled.div`
  display: flex;
  gap: 50rem;

  .txt-shuffle-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
  }

  .txt-shuffle {
    text-align: right;
  }

  ${({ $renderBottom }) =>
    $renderBottom &&
    css`
      position: relative;
      margin-top: 30rem;
      display: flex;
      justify-content: flex-end;
    `}
`;
